import React, { useState, useEffect } from "react";
import { addLeave, removeLeave, getLeaves } from "../api";
import { C, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Badge from "../components/Badge";

function countWorkingDays(start, end) {
  if (!start || !end) return 0;
  let count = 0;
  let cur = new Date(start + "T00:00:00Z");
  const endD = new Date(end + "T00:00:00Z");
  while (cur <= endD) {
    const dow = cur.getUTCDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return count;
}

function ConfirmBox({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      background: C.warningBg,
      border: `1px solid ${C.warning}55`,
      borderRadius: LAYOUT.radiusSm,
      padding: "14px 16px",
      marginTop: 8,
    }}>
      <div style={{ fontSize: 13, color: C.warning, fontWeight: 600, marginBottom: 10 }}>{message}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn small onClick={onConfirm} style={{ background: C.error }}>Yes, remove</Btn>
        <Btn small variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

function loadStoredDailyPlan() {
  try {
    const plans = JSON.parse(localStorage.getItem("ap_plans") || "{}");
    return plans.daily || null;
  } catch {
    return null;
  }
}

const fieldStyle = {
  width: "100%",
  padding: "10px 12px",
  border: `1.5px solid ${C.border}`,
  borderRadius: LAYOUT.radiusSm,
  fontSize: 14,
  boxSizing: "border-box",
  fontFamily: "inherit",
  background: C.surface,
  color: C.text,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  display: "block",
  marginBottom: 4,
};

export default function LeaveManager({ currentPlan: currentPlanProp, onPlanUpdated }) {
  const [currentPlan, setCurrentPlan] = useState(() => currentPlanProp || loadStoredDailyPlan());
  const [leaves, setLeaves] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const workingDays = countWorkingDays(startDate, endDate);

  useEffect(() => {
    if (currentPlanProp) setCurrentPlan(currentPlanProp);
    else setCurrentPlan(loadStoredDailyPlan());
  }, [currentPlanProp]);

  useEffect(() => {
    getLeaves()
      .then((r) => { if (r.success) setLeaves(r.leaveEvents || []); })
      .catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!startDate || !endDate) {
      setMsg({ type: "error", text: "Start and end dates are required" });
      return;
    }
    if (endDate < startDate) {
      setMsg({ type: "error", text: "End date must be on or after start date" });
      return;
    }
    if (!currentPlan) {
      setMsg({ type: "error", text: "Generate a plan first — then you can add leaves" });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res = await addLeave(startDate, endDate, reason);
      if (res.success) {
        setLeaves(res.leaveEvents || []);
        if (res.plan) {
          setCurrentPlan(res.plan);
          try {
            const plans = JSON.parse(localStorage.getItem("ap_plans") || "{}");
            localStorage.setItem("ap_plans", JSON.stringify({ ...plans, daily: res.plan }));
          } catch { /* ignore */ }
        }
        onPlanUpdated && onPlanUpdated(res.plan, res.version, res.diff);
        setMsg({
          type: "success",
          text: res.message || `Leave added. Schedule pushed forward by ${workingDays} working day(s). Saved as Version ${res.version}.`,
        });
        setStartDate("");
        setEndDate("");
        setReason("");
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Failed to add leave" });
    }
    setLoading(false);
  };

  const handleRemoveConfirm = async (leaveId) => {
    setConfirmId(null);
    setLoading(true);
    setMsg(null);
    try {
      const res = await removeLeave(leaveId);
      if (res.success) {
        setLeaves(res.leaveEvents || []);
        if (res.plan) {
          setCurrentPlan(res.plan);
          try {
            const plans = JSON.parse(localStorage.getItem("ap_plans") || "{}");
            localStorage.setItem("ap_plans", JSON.stringify({ ...plans, daily: res.plan }));
          } catch { /* ignore */ }
        }
        onPlanUpdated && onPlanUpdated(res.plan, null, res.diff);
        setMsg({
          type: "success",
          text: "Leave removed. Schedule has been fully restored to the original for those dates.",
        });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Failed to remove leave" });
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "8px 4px 28px" }}>
      <PageHeader
        title="Leave Manager"
        subtitle="Add college leaves or holidays. Tasks on those days move to the next working day. Remove a leave to restore the original schedule."
        icon="🏖"
      />

      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 14px", color: C.text }}>Add leave period</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>End date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={fieldStyle} />
          </div>
        </div>

        {startDate && endDate && endDate >= startDate && (
          <div style={{
            background: C.warningBg,
            border: `1px solid ${C.warning}44`,
            borderRadius: LAYOUT.radiusSm,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
            color: C.warning,
            lineHeight: 1.45,
          }}>
            <strong>Impact:</strong> {workingDays} working day(s) of leave — schedule pushes forward by the same amount. No tasks lost.
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Reason</label>
          <input
            type="text"
            placeholder="e.g. Principal announcement, government holiday"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {msg && (
          <div style={{
            padding: "10px 14px",
            borderRadius: LAYOUT.radiusSm,
            marginBottom: 12,
            fontSize: 13,
            fontWeight: 600,
            background: msg.type === "error" ? C.errorBg : C.successBg,
            color: msg.type === "error" ? C.error : C.success,
            border: `1px solid ${msg.type === "error" ? `${C.error}33` : `${C.success}33`}`,
          }}>
            {msg.text}
          </div>
        )}

        <Btn onClick={handleAdd} disabled={loading}>
          {loading ? "Processing…" : "Add leave & push schedule"}
        </Btn>
      </Card>

      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 14px", color: C.text }}>
          Active leave events ({leaves.length})
        </h3>

        {leaves.length === 0 ? (
          <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>No leave events yet.</p>
        ) : (
          leaves.map((leave) => {
            const days = countWorkingDays(leave.startDate, leave.endDate);
            const isConfirming = confirmId === leave.id;
            return (
              <div key={leave.id} style={{ marginBottom: 10 }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: LAYOUT.radiusSm,
                  background: C.warningBg,
                  border: `1px solid ${C.warning}33`,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {leave.startDate} → {leave.endDate}
                      <Badge bg={C.accentSoft} color="#8A6A00">
                        {days} working day{days !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{leave.reason || "—"}</div>
                  </div>
                  <Btn
                    small
                    variant="outline"
                    disabled={loading}
                    onClick={() => setConfirmId(isConfirming ? null : leave.id)}
                    style={{ borderColor: `${C.error}55`, color: C.error, background: C.errorBg }}
                  >
                    Remove
                  </Btn>
                </div>

                {isConfirming && (
                  <ConfirmBox
                    message={`Remove this leave (${leave.startDate} to ${leave.endDate})? The schedule will be restored for these ${days} day(s).`}
                    onConfirm={() => handleRemoveConfirm(leave.id)}
                    onCancel={() => setConfirmId(null)}
                  />
                )}
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}
